import React, { useEffect } from 'react';
import { useTypedSelector } from '../../shared/hooks/useTypedSelector';
import { useDispatch } from 'react-redux';
import * as actions from '../../store/actions';
import {
    Box,
    makeStyles,
    MenuItem,
    Select,
    Typography,
} from '@material-ui/core';
import { ITheme } from '../../themes/custom/ITheme';
import { useTranslation } from 'react-i18next';
import { Skeleton } from '@mui/material';
import usePrices from '../../shared/hooks/usePrices';

interface Props {}

const useStyles = makeStyles((theme: ITheme) => {
    return {
        select: {
            marginBottom: '9px',
            padding: '0px',
            '& .MuiSelect-outlined': {
                border: '1px solid ' + theme.custom.palette.grey,
                borderRadius: '7px',
                padding: '0px 35px 0 5px',
                height: '28px',
                lineHeight: '28px',
            },
        },
        description: {
            fontSize: '15px',
            border: '1px solid ' + theme.custom.palette.grey,
            textAlign: 'justify',
            borderRadius: '7px',
            color: theme.custom.palette.grey,
            padding: '10px',
        },
        descriptionName: {
            width: '100%',
            color: theme.custom.palette.black,
        },
        showMore: {
            textAlign: 'right',
            textDecoration: 'underline',
            cursor: 'pointer',
        },
    };
});
const DESCRIPTION_THRESHOLD = 110;

const SelectFabric: React.FC<Props> = (props) => {
    const classes = useStyles();
    const dispatch = useDispatch();

    const [showMore, setShowMore] = React.useState<boolean>(false);
    const { t } = useTranslation();

    const { data: productData, loading: productLoading } = useTypedSelector(
        (state) => state.od.loadProduct
    );
    const { data: selectedData } = useTypedSelector(
        (state) => state.od.selected
    );
    const { displayPrice } = usePrices(productData?.currency);

    const handleFabricChange = (
        event: React.ChangeEvent<{ value: unknown }>
    ) => {
        const id = event.target.value as number;
        if (productData && productData.fabrics.length > 0) {
            const fabric = productData.fabrics
                .filter((fabric) => fabric.idAttribute === id)
                .pop();
            if (fabric) {
                dispatch(actions.setSelectedFabric(fabric));
            }
        }
    };

    useEffect(() => {
        if (productData && productData.fabrics.length > 0) {
            const fabric = productData.fabrics
                .filter((fabric) => fabric.default)
                .pop();
            if (fabric) {
                dispatch(actions.setSelectedFabric(fabric));
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [productData]);

    return (
        <React.Fragment>
            {productLoading && (
                <React.Fragment>
                    <Box mb={1}>
                        <Skeleton
                            variant="rectangular"
                            width={400}
                            height={28}
                            animation="wave"
                        />
                    </Box>
                    <Skeleton
                        variant="rectangular"
                        width={'100%'}
                        height={40}
                        animation="wave"
                    />
                </React.Fragment>
            )}
            {!productLoading && (
                <React.Fragment>
                    <Select
                        value={
                            (selectedData &&
                                selectedData.fabric?.idAttribute) ??
                            ''
                        }
                        onChange={handleFabricChange}
                        label={t('Selected fabric:')}
                        variant={'outlined'}
                        className={classes.select}
                    >
                        {productData &&
                            productData.fabrics.map((fabric) => (
                                <MenuItem
                                    key={fabric.idAttribute}
                                    value={fabric.idAttribute}
                                >
                                    {fabric.name}
                                    {fabric.price > 0 && (
                                        <span>
                                            (+ {displayPrice(fabric.price)})
                                        </span>
                                    )}
                                </MenuItem>
                            ))}
                    </Select>
                    <Box className={classes.description}>
                        {selectedData && selectedData.fabric && (
                            <React.Fragment
                                key={selectedData.fabric.idAttribute}
                            >
                                <strong className={classes.descriptionName}>
                                    {selectedData.fabric.name}
                                </strong>
                                <br />
                                {!showMore &&
                                    selectedData.fabric.description.length >
                                        DESCRIPTION_THRESHOLD && (
                                        <React.Fragment>
                                            <Typography>
                                                {selectedData.fabric.description.substr(
                                                    0,
                                                    DESCRIPTION_THRESHOLD
                                                )}
                                                ..
                                            </Typography>
                                            <Typography
                                                onClick={() =>
                                                    setShowMore(true)
                                                }
                                                className={classes.showMore}
                                            >
                                                {t('More info')}
                                            </Typography>
                                        </React.Fragment>
                                    )}
                                {showMore && (
                                    <React.Fragment>
                                        <Typography>
                                            {selectedData.fabric.description}
                                        </Typography>
                                        <Typography
                                            onClick={() => setShowMore(false)}
                                            className={classes.showMore}
                                        >
                                            {t('Less info')}
                                        </Typography>
                                    </React.Fragment>
                                )}
                            </React.Fragment>
                        )}
                    </Box>
                </React.Fragment>
            )}
        </React.Fragment>
    );
};

export default SelectFabric;
